import { apiClient } from '@/api/api'
import type { AxiosResponse } from 'axios'
import type {
  MyStylePageData,
  AvailableStyle,
  AvailableColor,
  AvailableBrand,
  ColorFamily,
  HeelPreferencePayload,
  HeelPreferenceResponse,
  InspirationPhoto,
  InspirationPhotoUploadUrlResponse,
  InspirationPhotosResponse,
  UserStyleDna,
} from '@/types/my-style'
import { INSPIRATION_PHOTO_CONTENT_TYPES, INSPIRATION_PHOTO_MAX_BYTES } from '@/types/my-style'
import { uploadFileToS3 } from '@/features/auth/api/profile'

export const getMyStyleData = async (): Promise<AxiosResponse<MyStylePageData>> => {
  return apiClient.get<MyStylePageData>('/user/style')
}

/**
 * T-168 / BE-217 — `audience`: the catalogue of another person (a gift recipient), not the
 * shopper's own. Until the backend reads the param it is ignored and the own catalogue comes back.
 */
export const getAvailableStyles = async (
  audience?: 'woman' | 'man',
): Promise<AxiosResponse<AvailableStyle[]>> => {
  // Бекенд возвращает массив напрямую
  return apiClient.get<AvailableStyle[]>('/user/style/available-styles', {
    params: audience ? { audience } : undefined,
  })
}

export const getAvailableColors = async (): Promise<AxiosResponse<AvailableColor[]>> => {
  // Бекенд возвращает массив напрямую
  return apiClient.get<AvailableColor[]>('/user/style/available-colors')
}

/** BE-144 — the colour families of the picker chips, active only, names in `Accept-Language`. */
export const getColorFamilies = async (): Promise<ColorFamily[]> => {
  const response = await apiClient.get<{ families: ColorFamily[] }>('/user/style/color-families')
  return [...(response.data?.families ?? [])].sort((a, b) => a.sortOrder - b.sortOrder)
}

export const getAvailableBrands = async (): Promise<AxiosResponse<AvailableBrand[]>> => {
  // Бекенд возвращает массив напрямую
  return apiClient.get<AvailableBrand[]>('/user/style/available-brands')
}

export const resetStyle = async (styleId: number): Promise<AxiosResponse<void>> => {
  return apiClient.delete<void>(`/user/style/styles/${styleId}`)
}

export const resetAllStyles = async (): Promise<AxiosResponse<void>> => {
  return apiClient.delete<void>('/user/style/styles')
}

/** `DELETE /user/style` — styles, colors and brands in one go (204). */
export const resetStyleProfile = async (): Promise<AxiosResponse<void>> => {
  return apiClient.delete<void>('/user/style')
}

export interface AddStylesPayload {
  styleIds: number[]
}

export const addStyles = async (payload: AddStylesPayload): Promise<AxiosResponse<void>> => {
  // Interceptor преобразует camelCase в snake_case автоматически
  return apiClient.post<void>('/user/style/styles', payload)
}

export const removeColor = async (colorId: number): Promise<AxiosResponse<void>> => {
  return apiClient.delete<void>(`/user/style/colors/${colorId}`)
}

export interface AddColorsPayload {
  colorIds: number[]
}

export const addColors = async (payload: AddColorsPayload): Promise<AxiosResponse<void>> => {
  // Interceptor преобразует camelCase в snake_case автоматически
  return apiClient.post<void>('/user/style/colors', payload)
}

export const removeBrand = async (brandId: number): Promise<AxiosResponse<void>> => {
  return apiClient.delete<void>(`/user/style/brands/${brandId}`)
}

export interface AddBrandsPayload {
  brandIds: number[]
}

export const addBrands = async (payload: AddBrandsPayload): Promise<AxiosResponse<void>> => {
  // Interceptor преобразует camelCase в snake_case автоматически
  return apiClient.post<void>('/user/style/brands', payload)
}

/**
 * BE-136 — `DELETE /user/style/{style_id}`: the alias of `DELETE /user/style/styles/{id}`,
 * the same use case; the card's «Remove» (`FairProfileV2StyleCard`) calls it.
 */
export const removeStyle = async (styleId: number): Promise<AxiosResponse<void>> => {
  return apiClient.delete<void>(`/user/style/${styleId}`)
}

/**
 * BE-139 — `PATCH /user/style/heel-preference`: writes both what the profile shows and
 * what the level reads. `never` clears the height.
 */
export const updateHeelPreference = async (
  payload: HeelPreferencePayload,
): Promise<AxiosResponse<HeelPreferenceResponse>> => {
  // Interceptor преобразует camelCase в snake_case автоматически (ключи здесь уже плоские)
  return apiClient.patch<HeelPreferenceResponse>('/user/style/heel-preference', payload)
}

/** § 2.8 — `GET /user/style/dna`; 404 for an unknown user. */
export const getUserStyleDna = async (): Promise<AxiosResponse<UserStyleDna>> => {
  return apiClient.get<UserStyleDna>('/user/style/dna')
}

/* ——— Inspiration photos (BE-140, § 3.3) ——— */

export const createStylePhotoUploadUrl = async (file: File) => {
  // Interceptor преобразует camelCase в snake_case автоматически
  return apiClient.post<InspirationPhotoUploadUrlResponse>('/user/style/photos/upload-url', {
    contentType: file.type,
    fileName: file.name,
    fileSize: file.size,
  })
}

export const confirmStylePhoto = async (photoId: string) => {
  return apiClient.post<InspirationPhoto>('/user/style/photos', { photoId })
}

export const getStylePhotos = async (page = 1, pageSize = 20) => {
  return apiClient.get<InspirationPhotosResponse>('/user/style/photos', {
    params: { page, page_size: pageSize },
  })
}

export const deleteStylePhoto = async (photoId: number): Promise<AxiosResponse<void>> => {
  return apiClient.delete<void>(`/user/style/photos/${photoId}`)
}

/** BE-187 — `DELETE /user/style/photos` → `{deleted: n}` (0 when the gallery is already empty). */
export async function deleteAllStylePhotos(): Promise<number> {
  const { data } = await apiClient.delete<{ deleted: number }>('/user/style/photos')
  return data?.deleted ?? 0
}

/** BE-142 — `DELETE /user/style/colors` → `{success: true}`: every favourite colour at once. */
export const clearColors = async (): Promise<AxiosResponse<{ success: boolean }>> => {
  return apiClient.delete<{ success: boolean }>('/user/style/colors')
}

/** BE-142 — `DELETE /user/style/brands` → `{success: true}`: every favourite brand at once. */
export const clearBrands = async (): Promise<AxiosResponse<{ success: boolean }>> => {
  return apiClient.delete<{ success: boolean }>('/user/style/brands')
}

export class InspirationPhotoRejectedError extends Error {
  constructor(readonly reason: 'content_type' | 'file_size') {
    super(`inspiration photo rejected: ${reason}`)
  }
}

/**
 * The three-step upload: presigned url → PUT the bytes with the same Content-Type →
 * confirm within 15 minutes. The client checks what the server would answer 400 to
 * before asking for the url, so a wrong file never costs a request.
 */
export async function uploadStylePhoto(file: File): Promise<InspirationPhoto> {
  if (!(INSPIRATION_PHOTO_CONTENT_TYPES as readonly string[]).includes(file.type)) {
    throw new InspirationPhotoRejectedError('content_type')
  }
  if (file.size > INSPIRATION_PHOTO_MAX_BYTES) {
    throw new InspirationPhotoRejectedError('file_size')
  }
  const { data: ticket } = await createStylePhotoUploadUrl(file)
  await uploadFileToS3(file, ticket.uploadUrl, file.type)
  const { data } = await confirmStylePhoto(ticket.photoId)
  return data
}
